import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, auth } from "../lib/api";
import AdminOffers from "../components/AdminOffers";
import AdminSettings from "../components/AdminSettings";
import AdminPartners from "../components/AdminPartners";
import RouteSeo from "../components/RouteSeo";
import {
  Lock, Download, Trash2, Search, Users, CheckCircle2, Clock, LogOut, Inbox,
  FileJson, FileSpreadsheet, UserPlus, ShieldCheck, KeyRound, Power,
} from "lucide-react";

// ---------- Login ----------
function Login({ onOk }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password: pass });
      auth.setSession(data.access_token, data.user);
      toast.success(`Welcome, ${data.user.name || data.user.email}`);
      onOk();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4">
      <Card className="w-full max-w-sm border p-8 shadow-lg">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-primary">
          <Lock className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-center font-heading text-xl font-bold">Admin Sign In</h1>
        <p className="mt-1 text-center text-sm text-muted-foreground">Loansupportindia enquiry dashboard</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pass">Password</Label>
            <Input id="pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} className="w-full">{loading ? "Signing in..." : "Sign In"}</Button>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/" className="text-primary hover:underline">← Back to website</Link>
        </p>
      </Card>
    </div>
  );
}

// ---------- helpers ----------
function download(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function toCSV(rows) {
  const headers = ["Name", "Mobile", "Email", "City", "Requirement", "Message", "Consent", "Source", "Submitted At"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(
    [r.name, r.mobile, r.email, r.city, r.type, r.message, r.consent ? "Yes" : "No", r.source, r.created_at].map(esc).join(",")
  ));
  return lines.join("\n");
}

// ---------- Enquiries tab ----------
function Enquiries({ isSuper }) {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries");
      setLeads(data);
    } catch { toast.error("Failed to load enquiries"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const types = useMemo(() => ["all", ...Array.from(new Set(leads.map((l) => l.type).filter(Boolean)))], [leads]);
  const filtered = useMemo(() => leads
    .filter((l) => (filter === "all" ? true : l.type === filter))
    .filter((l) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return [l.name, l.mobile, l.email, l.city, l.type].some((v) => (v || "").toLowerCase().includes(s));
    }), [leads, q, filter]);

  const removeOne = async (id) => {
    try { await api.delete(`/admin/enquiries/${id}`); toast.success("Enquiry deleted."); load(); }
    catch { toast.error("Delete failed"); }
  };
  const clearAll = async () => {
    try { await api.delete("/admin/enquiries"); toast.success("All enquiries cleared."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };
  const dlCSV = () => { if (!filtered.length) return toast.error("Nothing to download."); download(`lsi-leads-${Date.now()}.csv`, toCSV(filtered), "text/csv;charset=utf-8;"); toast.success("CSV downloaded."); };
  const dlJSON = () => { if (!filtered.length) return toast.error("Nothing to download."); download(`lsi-leads-${Date.now()}.json`, JSON.stringify(filtered, null, 2), "application/json"); toast.success("JSON downloaded."); };

  const withConsent = leads.filter((l) => l.consent).length;
  const today = leads.filter((l) => (l.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10)).length;
  const stats = [
    { icon: Users, label: "Total Enquiries", value: leads.length },
    { icon: CheckCircle2, label: "With Consent", value: withConsent },
    { icon: Clock, label: "Today", value: today },
    { icon: Inbox, label: "Shown (filtered)", value: filtered.length },
  ];

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="border p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{s.label}</p>
                <p className="mt-1 font-heading text-2xl font-bold">{s.value}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><s.icon className="h-5 w-5" /></div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-6 border">
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search name, mobile, email, city…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All requirements" : t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={dlCSV}><FileSpreadsheet className="mr-1.5 h-4 w-4" />CSV</Button>
            <Button variant="outline" onClick={dlJSON}><FileJson className="mr-1.5 h-4 w-4" />JSON</Button>
            {isSuper && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive"><Trash2 className="mr-1.5 h-4 w-4" />Clear</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Clear all enquiries?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes all leads from the database. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={clearAll} className="bg-destructive hover:bg-destructive/90">Clear All</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/40">
                <TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Email</TableHead>
                <TableHead>City</TableHead><TableHead>Requirement</TableHead><TableHead>Source</TableHead>
                <TableHead>Consent</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="py-16 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="py-16 text-center text-muted-foreground">
                  <Inbox className="mx-auto mb-3 h-10 w-10 opacity-40" />No enquiries yet.
                </TableCell></TableRow>
              ) : filtered.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>{l.mobile}</TableCell>
                  <TableCell>{l.email || "—"}</TableCell>
                  <TableCell>{l.city || "—"}</TableCell>
                  <TableCell>{l.type || "—"}</TableCell>
                  <TableCell className="capitalize">{l.source || "—"}</TableCell>
                  <TableCell>{l.consent ? <span className="font-medium text-brand-green">Yes</span> : "No"}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(l.created_at).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => removeOne(l.id)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

// ---------- Team tab (superadmin) ----------
function Team({ me }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch { toast.error("Failed to load team"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("Team member created.");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "admin" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: !u.active }); load(); toast.success(u.active ? "Deactivated" : "Activated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.id}`, { role }); load(); toast.success("Role updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const resetPw = async (u) => {
    const pw = prompt(`Set a new password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    try { await api.patch(`/admin/users/${u.id}`, { password: pw }); toast.success("Password updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const remove = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="border">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h2 className="font-heading text-lg font-bold">Team & Access Control</h2>
          <p className="text-sm text-muted-foreground">Superadmins manage everything; admins manage enquiries only.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><UserPlus className="mr-1.5 h-4 w-4" />Add Member</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>Create a new admin account with a role.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" /></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (enquiries only)</SelectItem>
                    <SelectItem value="superadmin">Superadmin (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Status</TableHead><TableHead>Added By</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name || "—"} {u.id === me.id && <Badge variant="secondary" className="ml-1">You</Badge>}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === me.id}>
                    <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="superadmin">Superadmin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{u.active ? <Badge className="bg-brand-green">Active</Badge> : <Badge variant="secondary">Disabled</Badge>}</TableCell>
                <TableCell className="text-muted-foreground">{u.created_by || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" title="Reset password" onClick={() => resetPw(u)}><KeyRound className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Toggle active" disabled={u.id === me.id} onClick={() => toggleActive(u)}><Power className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" disabled={u.id === me.id}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
                          <AlertDialogDescription>This account will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(u)} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// ---------- Shell ----------
export default function Admin() {
  const [me, setMe] = useState(auth.getUser());
  const [authed, setAuthed] = useState(auth.isAuthed());
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (auth.isAuthed()) {
      api.get("/auth/me").then(({ data }) => { setMe(data); auth.setSession(localStorage.getItem("lsi_token"), data); setAuthed(true); })
        .catch(() => { auth.clear(); setAuthed(false); });
    }
  }, []);

  if (!authed) return <Login onOk={() => { setMe(auth.getUser()); setAuthed(true); }} />;

  const isSuper = me?.role === "superadmin";
  const logout = () => { auth.clear(); setAuthed(false); };

  return (
    <div className="min-h-screen bg-secondary/30">
      <RouteSeo />
      <div className="border-b bg-background">
        <div className="container-page flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-full.png" alt="Loansupportindia" className="h-8 w-auto" />
            <span className="hidden text-sm text-muted-foreground sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium">{me?.name || me?.email}</div>
              <div className="text-xs text-muted-foreground capitalize flex items-center gap-1 justify-end">
                <ShieldCheck className="h-3 w-3" />{me?.role}
              </div>
            </div>
            <Button asChild variant="ghost" size="sm"><Link to="/">View Site</Link></Button>
            <Button variant="outline" size="sm" onClick={logout}><LogOut className="mr-1.5 h-4 w-4" />Logout</Button>
          </div>
        </div>
      </div>

      <div className="container-page py-8">
        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="content">Site Content</TabsTrigger>
            {isSuper && <TabsTrigger value="team">Team & Access</TabsTrigger>}
          </TabsList>
        </Tabs>
        {tab === "team" && isSuper ? <Team me={me} />
          : tab === "offers" ? <AdminOffers />
          : tab === "partners" ? <AdminPartners />
          : tab === "content" ? <AdminSettings />
          : <Enquiries isSuper={isSuper} />}
      </div>
    </div>
  );
}
